<!--
 * @Description: 代办供应商-开票记录（16种按钮组合）
 * @Date: 2026-06-05
-->
<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  />
</template>
<script lang="tsx" setup>
import { ref } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useSelectColumn,
  BaseButtonMore,
  useCreateModal,
  BaseButton,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';
import { formatter } from '@/utils';
import {
  queryInvoiceList,
  downloadTaxForId,
  jlBatchDownload,
  openReject,
  delayInvoice,
  invoiceExport
} from '@V/invoice/src/data-source/apis';
import { DBGYS_ORDER_TYPE } from '@V/invoice/src/constants';
import {
  enterpriseNameSearch,
  supplierNameSearch,
  invoiceTypeSearch,
  blueInvoiceStatusSearch,
  redInvoiceStatusSearch,
  timeRangeSearch,
  updateTimeSearch
} from '@V/invoice/src/fields/searchs';
import {
  invoiceOrderNoCol,
  enterpriseNameCol,
  supplierNameCol,
  orderTypeCol,
  invoiceTypeCol,
  blueInvoiceStatusCol,
  redInvoiceStatusCol,
  applyTimeCol,
  updateTimeCol,
  invoiceAmountCol
} from '@V/invoice/src/fields/columns';
import InvoiceDetailGtDialog from '../invoice-detail-dialog/invoice-detail-gt-dialog.vue';
import { useBatchDownload } from '@V/invoice/src/hooks';
import type { GtButtonSource } from '@V/invoice/src/types';

const props = withDefaults(
  defineProps<{
    invoiceTabType?: number;
  }>(),
  {
    invoiceTabType: 1
  }
);

const xrkTableRef = ref();
const { selectionField, download } = useBatchDownload({
  xrkTableRef,
  ajax: jlBatchDownload,
  options: {
    passStatus: [2, 6, 12]
  }
});

const getListAjax = async params => {
  return queryInvoiceList({
    ...params,
    type: props.invoiceTabType,
    invoiceBusinessTypeList: [7, 10]
  });
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    enterpriseNameSearch(),
    supplierNameSearch(),
    useSelectColumn({
      prop: 'orderMixtureType',
      label: '订单类型',
      config: {
        placeholder: '请选择',
        clearable: true,
        options: DBGYS_ORDER_TYPE
      }
    }),
    invoiceTypeSearch(),
    blueInvoiceStatusSearch(),
    redInvoiceStatusSearch(),
    timeRangeSearch(),
    updateTimeSearch()
  ].filter(Boolean),
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置'
  },
  otherComponents: [
    {
      render() {
        return (
          <>
            <BaseButton
              type="primary2"
              click={async () => {
                await download();
              }}
            >
              批量导出
            </BaseButton>
          </>
        );
      }
    }
  ]
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    selectionField(),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px',
      fixed: 'left'
    }),
    invoiceOrderNoCol(),
    enterpriseNameCol(),
    supplierNameCol(),
    orderTypeCol(DBGYS_ORDER_TYPE),
    invoiceAmountCol(),
    useTableColumn({
      label: '票面金额(元)',
      prop: 'sumAmount',
      formatter: ({ cellValue }) => {
        if (cellValue === null || cellValue === undefined || cellValue === 0) {
          return '--';
        }
        return formatter.fen2yuan(cellValue);
      }
    }),
    invoiceTypeCol(),
    useTableColumn({
      label: '开票类目',
      prop: 'itemName',
      formatter: ({ cellValue }) => {
        if (!cellValue) return '--';
        if (Array.isArray(cellValue)) {
          return cellValue.join(' / ');
        }
        return String(cellValue).split(',').join(' / ');
      }
    }),
    blueInvoiceStatusCol(),
    redInvoiceStatusCol(),
    applyTimeCol(),
    updateTimeCol(),

    useTableColumn({
      label: '操作',
      prop: 'handlerCol',
      fixed: 'right',
      width: '180px',
      render: ({ row }) => {
        const BS = row.blueInvoiceStatus;
        const RS = row.redInvoiceStatus;

        // 详情弹窗
        const openGtDetail = (buttonSource: GtButtonSource) => {
          useCreateModal(InvoiceDetailGtDialog, {
            id: row.id,
            scene: 11,
            buttonSource,
            onSuccess() {
              xrkTableRef.value?.initCurrentList?.();
            }
          });
        };

        // 拒绝开票
        const onRejectInvoice = () => {
          XrkMessageBox.prompt('请填写拒绝原因（最多200字）', '拒绝开票', {
            confirmButtonText: '确认',
            cancelButtonText: '取消',
            inputValidator: val => {
              if (!val?.trim()) return '请填写拒绝原因';
              if (val.length > 200) return '原因不能超过200字';
              return true;
            }
          })
            .then(async val => {
              await openReject({
                invoiceApplyId: row.id,
                invoiceStatus: 0,
                invoiceType: 1,
                noPassReason: val.value
              });
              XrkMessage.success('已拒绝开票');
              xrkTableRef.value.initCurrentList();
            })
            .catch(() => {
              /* noop */
            });
        };

        // 延迟开票
        const onDelayInvoice = () => {
          XrkMessageBox.prompt('请填写延迟原因（最多200字）', '延迟开票', {
            confirmButtonText: '确认',
            cancelButtonText: '取消',
            inputValidator: val => {
              if (!val?.trim()) return '请填写延迟原因';
              if (val.length > 200) return '原因不能超过200字';
              return true;
            }
          })
            .then(async val => {
              await delayInvoice({
                invoiceApplyId: row.id,
                delayInvoiceReason: val.value
              });
              XrkMessage.success('已延迟开票');
              xrkTableRef.value.initCurrentList();
            })
            .catch(() => {
              /* noop */
            });
        };

        // 按钮独立条件判断
        const btns = [
          // 详情：始终显示
          {
            content: '详情',
            click: () => openGtDetail(11)
          },
          // 上传蓝票：BS ∈ [0, 11] 且 RS 为空或红票已驳回
          [0, 11].includes(BS) &&
            (!RS || [7, 9].includes(RS)) && {
              content: '上传蓝票',
              click: () => openGtDetail(1)
            },
          // 修改上传(蓝)：BS === 3
          BS === 3 && {
            content: '修改上传',
            click: () => openGtDetail(3)
          },
          // 补充上传(蓝)：BS === 12 && (!RS || [6,7,9,10].includes(RS))
          BS === 12 &&
            (!RS || [6, 7, 9, 10].includes(RS)) && {
              content: '补充上传(蓝)',
              click: () => openGtDetail(2)
            },
          // 上传红票：BS === 2 && RS === 4
          [2, 12].includes(BS) &&
            RS === 4 && {
              content: '上传红票',
              click: () => openGtDetail(4)
            },
          // 修改上传红票：BS === 2 && RS === 7
          [2, 12].includes(BS) &&
            RS === 7 && {
              content: '修改上传红票',
              click: () => openGtDetail(5)
            },
          // 补充红票：BS ∈ [2, 12] && RS === 10（代办供应商专属）
          [2, 12].includes(BS) &&
            RS === 10 && {
              content: '补充红票',
              click: () => openGtDetail(15)
            },
          // 拒绝开票：BS === 0
          BS === 0 && {
            content: '拒绝开票',
            click: () => onRejectInvoice()
          },
          // 延迟开票：BS === 0 且 invoiceBusinessType === 7
          BS === 0 &&
            row.invoiceBusinessType === 7 && {
              content: '延迟开票',
              click: () => onDelayInvoice()
            },
          // 下载开票信息：始终显示
          {
            content: '下载开票信息',
            click: async () => {
              await invoiceExport({ invoiceId: row.id });
            }
          },
          // 下载发票：[2,12].includes(BS) || [6,10].includes(RS)
          ([2, 12].includes(BS) || [6, 10].includes(RS)) && {
            content: '下载发票',
            click: async () => {
              await downloadTaxForId({ invoiceId: row.id });
            }
          }
        ].filter(Boolean);

        return (
          <BaseButtonMore
            key={Date.now()}
            maxCount={3}
            style={{ 'justify-content': 'right' }}
            btns={btns}
          ></BaseButtonMore>
        );
      }
    })
  ].filter(Boolean)
});
</script>
